import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' }
});

// Automatically attach the login token to every request
api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

/* W-053 step 3a: THE ONE SESSION WRITER. The token is written FIRST because the request interceptor reads it, so the
   request after a login or a password change carries the new token (a password change revokes the old one). */
export const storeSession = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
};

/* W-053 step 3a: THE FORCED-CHANGE SIGNAL. A login that must set a new password is answered 403 code MUST_CHANGE_PASSWORD
   by every route except /me and change-password. This interceptor is the ONE place that recognises it: it marks the stored
   user and calls the listener the app shell registers (App.js), which shows the "Set your new password" screen. */
let mustChangeListener = null;
export const onMustChangePassword = fn => {
    mustChangeListener = fn;
    return () => { if (mustChangeListener === fn) mustChangeListener = null; };
};

/* The login's OWN refusal (401 'Invalid credentials') is an answer for the login page to show, not a lost session:
   wiping and reloading there erased the message before anyone could read it. */
const isLoginRequest = config =>
    !!config && String(config.method || '').toLowerCase() === 'post' && /\/api\/auth\/login$/.test(config.url || '');

// A 401 from any other request means the session is gone: sign out and go to the login page (as before).
api.interceptors.response.use(
    response => response,
    error => {
        const status = error.response?.status;
        if (status === 401 && !isLoginRequest(error.config)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        } else if (status === 403 && error.response?.data?.code === 'MUST_CHANGE_PASSWORD') {
            try {
                const u = JSON.parse(localStorage.getItem('user') || 'null');
                if (u && !u.must_change_password) localStorage.setItem('user', JSON.stringify({ ...u, must_change_password: true }));
            } catch (e) { /* an unreadable stored user is left as it is */ }
            if (mustChangeListener) mustChangeListener();
        }
        return Promise.reject(error);
    }
);

// ============================================================
// AUTH
// ============================================================
export const login = (email, password) =>
    api.post('/api/auth/login', { email, password });

export const getMe = () =>
    api.get('/api/auth/me');

/* W-053: change your own password. The response carries a FRESH token (the old one is revoked), stored here before the
   promise resolves, so no later request can go out with the revoked token. */
export const changePassword = async (current_password, new_password) => {
    const res = await api.post('/api/auth/change-password', { current_password, new_password });
    storeSession(res.data.token, res.data.user);
    return res;
};

// ============================================================
// USER MANAGEMENT (W-053 -- the Admin page's Add User tab)
// ============================================================
// every login of the company, active first (admins and the super admin; a viewer is refused)
export const listUsersManage = (companyId) =>
    api.get(`/api/${companyId}/users/manage`);

// { email, role: 'admin' | 'viewer' } -> 201 { user, temporary_password } (the password is shown ONCE, never stored)
export const createUser = (companyId, data) =>
    api.post(`/api/${companyId}/users`, data);

// { role?, is_active? } -> the row
export const updateUser = (companyId, userId, changes) =>
    api.patch(`/api/${companyId}/users/${userId}`, changes);

// -> { temporary_password } (shown ONCE, never stored)
export const resetUserPassword = (companyId, userId) =>
    api.post(`/api/${companyId}/users/${userId}/reset-password`);

// SUPER ADMIN only: { company_id, role } -> the row in its new company
export const moveUser = (userId, data) =>
    api.post(`/api/admin/users/${userId}/move`, data);

// ============================================================
// COMPANIES
// ============================================================
export const getCompanies = () =>
    api.get('/api/companies');

export const createCompany = (name, fiscal_year_end) =>
    api.post('/api/companies', { name, fiscal_year_end });

// ============================================================
// EMPLOYEES
// ============================================================
export const getEmployees = (companyId) =>
    api.get(`/api/companies/${companyId}/employees`);

export const getEmployeesWithActuals = (companyId) =>
    api.get(`/api/companies/${companyId}/employees-with-actuals`);

export const createEmployee = (companyId, data) =>
    api.post(`/api/companies/${companyId}/employees`, data);

export const updateEmployee = (companyId, id, data) =>
    api.put(`/api/companies/${companyId}/employees/${id}`, data);

export const patchEmployee = (companyId, id, data) =>
    api.patch(`/api/companies/${companyId}/employees/${id}`, data);

export const deleteEmployee = (companyId, id) =>
    api.delete(`/api/companies/${companyId}/employees/${id}`);

// ============================================================
// EMPLOYEE MONTHLY ACTUALS
// ============================================================
export const bulkUpsertEmployeeActuals = (companyId, employeeId, actuals) =>
    api.post(`/api/companies/${companyId}/employees/${employeeId}/actuals/bulk`, { actuals });

// ============================================================
// EMPLOYEE RAISES
// ============================================================
export const getEmployeeRaises = (companyId, employeeId) =>
    api.get(`/api/companies/${companyId}/employees/${employeeId}/raises`);

export const createEmployeeRaise = (companyId, employeeId, data) =>
    api.post(`/api/companies/${companyId}/employees/${employeeId}/raises`, data);

export const deleteEmployeeRaises = (companyId, employeeId) =>
    api.delete(`/api/companies/${companyId}/employees/${employeeId}/raises`);

// ============================================================
// VENDORS
// ============================================================
export const getVendors = (companyId) =>
    api.get(`/api/companies/${companyId}/vendors`);

export const getVendorsWithActuals = (companyId) =>
    api.get(`/api/companies/${companyId}/vendors-with-actuals`);

export const createVendor = (companyId, data) =>
    api.post(`/api/companies/${companyId}/vendors`, data);

export const updateVendor = (companyId, id, data) =>
    api.put(`/api/companies/${companyId}/vendors/${id}`, data);

// Session 5d-1: the server returns 409 { error:'vendor_has_prepaid', message,
// tiedContracts[] } when the vendor has linked prepaid contracts. Axios rejects
// on 409, so callers read err.response.data.{message,tiedContracts} in catch and
// surface the block (do NOT swallow it). On success returns { deleted:true }.
export const deleteVendor = (companyId, id) =>
    api.delete(`/api/companies/${companyId}/vendors/${id}`);

// Session 5c bulk-edit: transactional single-column update across N vendors.
// Axios rejects on non-2xx (400/409/500) — caller's catch reads
// err.response.data.error. Wired to the confirmation modal in sc 5c-3.
export const bulkUpdateVendors = (companyId, { vendorIds, action, value }) =>
    api.patch(`/api/companies/${companyId}/vendors/bulk`, { vendorIds, action, value });

// ============================================================
// VENDOR MONTHLY ACTUALS
// ============================================================
export const getVendorActuals = (companyId, vendorId) =>
    api.get(`/api/companies/${companyId}/vendors/${vendorId}/actuals`);

export const bulkUpsertVendorActuals = (companyId, vendorId, actuals) =>
    api.post(`/api/companies/${companyId}/vendors/${vendorId}/actuals/bulk`, { actuals });

// ============================================================
// PREPAID CONTRACTS
// ============================================================
export const getPrepaidContracts = (companyId) =>
    api.get(`/api/companies/${companyId}/prepaid-contracts`);

export const createPrepaidContract = (companyId, data) =>
    api.post(`/api/companies/${companyId}/prepaid-contracts`, data);

// Session 5d-4a: field-only update. Server may reject with 409 prepaid_duplicate
// (6-col unique collision), 400 bad_vendor_id (FK), or 404 (no such contract).
// Axios rejects on those; callers read err.response.data.{error,message} in catch.
// (First caller is 5d-4b's PrepaidModal — no caller wired yet.)
export const updatePrepaidContract = (companyId, id, data) =>
    api.put(`/api/companies/${companyId}/prepaid-contracts/${id}`, data);

export const deletePrepaidContract = (companyId, id) =>
    api.delete(`/api/companies/${companyId}/prepaid-contracts/${id}`);

// ============================================================
// COMPANY SETTINGS
// ============================================================
export const getCompanySettings = (companyId) =>
    api.get(`/api/companies/${companyId}/settings`);

export const updateCompanySettings = (companyId, data) =>
    api.put(`/api/companies/${companyId}/settings`, data);

// Bi-weekly payroll, step 4: the company's pay schedule -- the WHOLE answer every time (a field that does not apply is
// omitted). Super admin only (a wizard route). 200 { pay_schedule, relinked, roster_reimport_required,
// roster_reimport_message }; 400 { error, code } and nothing is stored. Its one caller is the Step-5a panel.
export const savePaySchedule = (companyId, data) =>
    api.put(`/api/companies/${companyId}/pay-schedule`, data);

// Commission ship 5: how the books treat commissions -- the WHOLE answer every time (a field that does not apply is omitted).
// Super admin only (a wizard route). 200 { commission_accrual, relinked }; 400 { error, code } and nothing is stored. Its one
// caller is the Step-5 commission panel.
export const saveCommissionAccrual = (companyId, data) =>
    api.put(`/api/companies/${companyId}/commission-accrual`, data);

// ============================================================
// FIXED ASSETS
// ============================================================
export const getFixedAssets = (companyId) =>
    api.get(`/api/companies/${companyId}/fixed-assets`);

export const createFixedAsset = (companyId, data) =>
    api.post(`/api/companies/${companyId}/fixed-assets`, data);

export const updateFixedAsset = (companyId, id, data) =>
    api.put(`/api/companies/${companyId}/fixed-assets/${id}`, data);

export const deleteFixedAsset = (companyId, id) =>
    api.delete(`/api/companies/${companyId}/fixed-assets/${id}`);

// ============================================================
// INTANGIBLE ASSETS
// ============================================================
export const getIntangibleAssets = (companyId) =>
    api.get(`/api/companies/${companyId}/intangible-assets`);

export const createIntangibleAsset = (companyId, data) =>
    api.post(`/api/companies/${companyId}/intangible-assets`, data);

export const updateIntangibleAsset = (companyId, id, data) =>
    api.put(`/api/companies/${companyId}/intangible-assets/${id}`, data);

export const deleteIntangibleAsset = (companyId, id) =>
    api.delete(`/api/companies/${companyId}/intangible-assets/${id}`);

// ============================================================
// CHART OF ACCOUNTS
// ============================================================
export const getChartOfAccounts = (companyId) =>
    api.get(`/api/companies/${companyId}/chart-of-accounts`);

// REVENUE (legacy) -- DELETED 2026-10-02 (Group 3, F8.3): the eight revenue-models / revenue-lines /
// revenue-inputs / revenue-output clients. No tenant ever had a revenue model (revenue_models 0 rows;
// revenue_inputs / revenue_lines do not exist) and their server routes are retired.

// ============================================================
// MONTHLY FINANCIALS
// ============================================================
export const getFinancials = (companyId, statement) =>
    api.get(`/api/companies/${companyId}/financials`, { params: { statement } });

// ============================================================
// SCENARIOS
// ============================================================
// includeShared (scenario sharing, 2026-10-01): also the scenarios shared WITH the caller, each row carrying
// owner_id / owner_email / mine (and shared_with on the caller's own). Without it: the caller's own only, as before.
export const getScenarios = (companyId, opts) =>
    api.get(`/api/${companyId}/scenarios`, opts && opts.includeShared ? { params: { include: 'shared' } } : undefined);

// the company's logins (id + email) for the Share picker
export const getCompanyUsers = (companyId) =>
    api.get(`/api/${companyId}/users`);

// the WHOLE share set of one of the caller's scenarios (an empty list = private)
export const setScenarioShares = (companyId, scenarioId, userIds) =>
    api.put(`/api/${companyId}/scenarios/${scenarioId}/shares`, { user_ids: userIds });

export const createScenario = (companyId, name, inputs) =>
    api.post(`/api/${companyId}/scenarios`, { name, inputs });

export const updateScenario = (companyId, scenarioId, data) =>
    api.put(`/api/${companyId}/scenarios/${scenarioId}`, data);

export const deleteScenario = (companyId, scenarioId) =>
    api.delete(`/api/${companyId}/scenarios/${scenarioId}`);

// ============================================================
// DEFERRED REVENUE DATA
// ============================================================
export const getDefrevData = (companyId) =>
    api.get(`/api/companies/${companyId}/defrev-data`);

// ============================================================
// FUNDRAISING ROUNDS
// ============================================================
export const getFundraisingRounds = (companyId) =>
    api.get(`/api/companies/${companyId}/fundraising-rounds`);

export const createFundraisingRound = (companyId, data) =>
    api.post(`/api/companies/${companyId}/fundraising-rounds`, data);

export const updateFundraisingRound = (companyId, id, data) =>
    api.put(`/api/companies/${companyId}/fundraising-rounds/${id}`, data);

export const deleteFundraisingRound = (companyId, id) =>
    api.delete(`/api/companies/${companyId}/fundraising-rounds/${id}`);

// ============================================================
// AE QUOTA SETTINGS
// ============================================================
export const getAEQuotaSettings = (companyId) =>
    api.get(`/api/${companyId}/ae-quota-settings`);

export const updateAEQuotaSettings = (companyId, settings) =>
    api.put(`/api/${companyId}/ae-quota-settings`, settings);

// Billing terms P3: the Unbilled AR account link -- its own route, super admin only. body { line_key | null, expected_stamp }.
export const updateUnbilledArLink = (companyId, body) =>
    api.put(`/api/${companyId}/ae-quota-settings/unbilled-ar-link`, body);

// ============================================================
// COMPANY QUOTA SETTINGS
// ============================================================
export const getCompanyQuotaSettings = (companyId) =>
    api.get(`/api/${companyId}/company-quota-settings`);

export const updateCompanyQuotaSettings = (companyId, settings) =>
    api.put(`/api/${companyId}/company-quota-settings`, settings);

// ============================================================
// BS ASSUMPTIONS (Phase 6)
// ============================================================
export const getBSAssumptions = (companyId) =>
    api.get(`/api/${companyId}/bs-assumptions`);

export const updateBSAssumption = (companyId, lineId, updates) =>
    api.put(`/api/${companyId}/bs-assumptions`, { line_id: lineId, updates });

export const updateBSAssumptionsBulk = (companyId, items) =>
    api.put(`/api/${companyId}/bs-assumptions/bulk`, items);

// W-013: delete assumption rows for lines no longer in the spreadsheet (the Step-4 re-import).
export const deleteBSAssumptions = (companyId, lineIds) =>
    api.delete(`/api/${companyId}/bs-assumptions`, { data: { line_ids: lineIds } });

// ============================================================
// MISC REVENUE LINES
// ============================================================
export const getMiscRevenueLines = (companyId) =>
    api.get(`/api/${companyId}/misc-revenue-lines`);

// 9c: optional body — Step 9's auto-create passes { target_gl_line_key } (the Path-A route,
// a true derivation from the rmm row). The Revenue Build "+ Add" caller stays body-less.
export const createMiscRevenueLine = (companyId, data) =>
    api.post(`/api/${companyId}/misc-revenue-lines`, data);

export const updateMiscRevenueLine = (companyId, lineId, data) =>
    api.put(`/api/${companyId}/misc-revenue-lines/${lineId}`, data);

export const deleteMiscRevenueLine = (companyId, lineId) =>
    api.delete(`/api/${companyId}/misc-revenue-lines/${lineId}`);

export const reorderMiscRevenueLines = (companyId, items) =>
    api.put(`/api/${companyId}/misc-revenue-lines/reorder`, { items });

// ============================================================
// IMPLEMENTATION LINES
// ============================================================
export const getImplementationLines = (companyId) =>
    api.get(`/api/${companyId}/implementation-lines`);

export const createImplementationLine = (companyId, data) =>
    api.post(`/api/${companyId}/implementation-lines`, data);

export const updateImplementationLine = (companyId, lineId, data) =>
    api.put(`/api/${companyId}/implementation-lines/${lineId}`, data);

export const deleteImplementationLine = (companyId, lineId) =>
    api.delete(`/api/${companyId}/implementation-lines/${lineId}`);

// ============================================================
// ONBOARDING STATE
// ============================================================
export const getOnboardingState = (companyId) =>
    api.get(`/api/${companyId}/onboarding-state`);

export const updateOnboardingState = (companyId, data) =>
    api.put(`/api/${companyId}/onboarding-state`, data);

// ============================================================
// HISTORICAL GL LINES
// ============================================================
export const importHistoricalGLLines = (companyId, rows) =>
    api.post(`/api/${companyId}/historical-gl-lines/import`, { rows });

export const getHistoricalGLLines = (companyId) =>
    api.get(`/api/${companyId}/historical-gl-lines`);

// ============================================================
// GL LINE MAPPINGS
// ============================================================
export const saveGLLineMappings = (companyId, mappings) =>
    api.post(`/api/${companyId}/gl-line-mappings/save`, { mappings });

export const getGLLineMappings = (companyId) =>
    api.get(`/api/${companyId}/gl-line-mappings`);

// Leaf reorder (Step-3 grid up/down). Transactional server-side; sort_order is
// display-only, so this cannot move a number — only the render sequence.
export const reorderGLLineMappings = (companyId, lines) =>
    api.put(`/api/${companyId}/gl-line-mappings/reorder`, { lines });

/* Rename a GL line's LABEL. Since Build 1 pinned each leaf's identity in
   gl_line_mappings.leaf_key, this cannot move a key — the server refuses to touch
   leaf_key/is_line_key/gl_name and only refreshes the one cache that serves the line.
   Returns { display_name, previous_display_name, leaf_key, key_changed:false,
   cache_refreshed, notices[] }; notices are INFORMATIONAL (a duplicate label is
   allowed) and are surfaced, never treated as an error. */
export const renameGLLineLabel = (companyId, id, display_name) =>
    api.put(`/api/${companyId}/gl-line-mappings/${id}/label`, { display_name });

// ============================================================
// ONBOARDING: IMPORT FINANCIALS
// ============================================================
export const importOnboardingFinancials = (companyId, payload) =>
    api.post(`/api/${companyId}/onboarding/import-financials`, payload);

// C-10: merge client-derived per-dg payroll keys into is_actuals (server read-modify-write).
export const mergeIsActualsPerDgPayroll = (companyId, actuals) =>
    api.post(`/api/${companyId}/onboarding/backfill-perdg-payroll`, { actuals });

export const getOnboardingFinancialsStatus = (companyId) =>
    api.get(`/api/${companyId}/onboarding/financials-status`);

// ============================================================
// ONBOARDING: IMPORT EMPLOYEES + DEPARTMENT MAPPINGS
// ============================================================
export const importOnboardingEmployees = (companyId, employees) =>
    api.post(`/api/${companyId}/onboarding/import-employees`, { employees });

export const saveDepartmentMappings = (companyId, mappings) =>
    api.post(`/api/${companyId}/department-mappings/save`, { mappings });

export const getDepartmentMappings = (companyId) =>
    api.get(`/api/${companyId}/department-mappings`);

export const renameBenefitComponent = (companyId, oldKey, newKey) =>
    api.post(`/api/${companyId}/onboarding/rename-benefit-component`, { old_key: oldKey, new_key: newKey });

// ============================================================
// ONBOARDING: IMPORT VENDORS
// ============================================================
export const importOnboardingVendors = (companyId, payload) =>
    api.post(`/api/${companyId}/onboarding/import-vendors`, payload);

export const getVendorMonthlyActuals = (companyId) =>
    api.get(`/api/${companyId}/vendor-monthly-actuals`);

export const saveVendorCategoryMappings = (companyId, mappings) =>
    api.post(`/api/${companyId}/vendor-category-mappings/save`, { mappings });

export const getVendorCategoryMappings = (companyId) =>
    api.get(`/api/${companyId}/vendor-category-mappings`);

// ============================================================
// REVENUE MODULE MAPPINGS (Onboarding Step 7)
// ============================================================
// W-081 part 2: `extra` carries { dr_reservations } (the Step-7 reservations); omitted -> the server keeps the stored set.
export const saveRevenueModuleMappings = (companyId, mappings, extra) =>
    api.post(`/api/${companyId}/revenue-module-mappings/save`, { mappings, ...(extra || {}) });

// W-081 part 2: the deferred-revenue accounts reserved at Step 7 for implementation revenue -> { reservations: {key: 'implementation'} | null }
export const getDrLineReservations = (companyId) =>
    api.get(`/api/${companyId}/dr-line-reservations`);

export const getRevenueModuleMappings = (companyId) =>
    api.get(`/api/${companyId}/revenue-module-mappings`);

// ============================================================
// ONBOARDING: ASSETS (Step 8)
// ============================================================
export const importOnboardingAssets = (companyId, payload) =>
    api.post(`/api/${companyId}/onboarding/import-assets`, payload);

export const getOnboardingAssetsStatus = (companyId) =>
    api.get(`/api/${companyId}/onboarding/assets-status`);

// ============================================================
// TAX RATES (global)
// ============================================================
export const getFederalTaxRates = () =>
    api.get('/api/tax-rates/federal');

// Multi-state build 2a: fetch ALL years (2025 + 2026) so the engine can resolve
// SUTA per forecast year. ModelProvider groups by tax_year into sutaByYear.
export const getStateTaxRates = () =>
    api.get('/api/tax-rates/state?all=true');

// Per-tenant experience-rated SUTA overrides (company_state_suta).
export const getCompanyStateSuta = (companyId) =>
    api.get(`/api/companies/${companyId}/state-suta`);

// Upsert one state's override. { suta_rate, suta_cap } — null = inherit the global
// default (written unconditionally, NOT COALESCE). Reverting = delete (below).
export const putCompanyStateSuta = (companyId, stateCode, body) =>
    api.put(`/api/companies/${companyId}/state-suta/${stateCode}`, body);

export const deleteCompanyStateSuta = (companyId, stateCode) =>
    api.delete(`/api/companies/${companyId}/state-suta/${stateCode}`);

// ── Session 9d: the Go Live flip (is_engine_ready) ─────────────────────────────
// LANDING 1: the endpoint runs every check and reports what it WOULD do, but does
// not flip (server-side GO_LIVE_ARMED=false). Body carries the CLIENT pre-flight
// verdict, which the server trusts (THE WALL) and records — it is evidence, not
// authorisation; the server backstop can still refuse.
export const setEngineReady = (companyId, body) =>
    api.put(`/api/companies/${companyId}/engine-ready`, body);

export default api;
